import { useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Table } from 'ui/table';
import { useClientPaginatedState } from 'ui/table/hooks/useClientPaginatedState';
import { TableHeader } from 'ui/tableHeader';
import { InputText } from 'ui/inputs/inputText';
import { TableRows } from 'ui/tableRows';
import { PageHeader } from 'ui/pageHeader';
import { PaginationFooter } from 'ui/paginationFooter';
import { AddNew16 } from 'ui/icons/aurora';
import { PrimaryButton } from 'ui/button';
import { FlexSpacer } from 'ui/flexSpacer';
import { selectFeatureFlags } from 'core/userContext';
import { selectSystemWebhooksTableRows } from '../selectors';
import {
  fetchSystemWebhooksRequest,
  createSystemWebhookRequest,
  deleteSystemWebhookRequest,
} from '../actions';
import { CreateSystemWebhookModal } from './CreateSystemWebhookModal';
import { DeleteSystemWebhookModal } from './DeleteSystemWebhookModal';
import { createColumns } from '../columns';
import { tableFilterFn } from '../utils';
import { WebhookParamProps } from '../types';

export const SystemWebhooks = () => {
  const systemWebhooksTableRows = useSelector(selectSystemWebhooksTableRows);
  const { SystemWebhookAdvancedFiltering } = useSelector(selectFeatureFlags);
  const dispatch = useDispatch();

  const {
    currentPage,
    itemsPerPage,
    numItems,
    rows,
    setCurrentPage,
    setItemsPerPage,
    setSortDirection,
    setSortedBy,
    sortDirection,
    sortedBy,
    filter,
    setFilter,
  } = useClientPaginatedState({
    rows: systemWebhooksTableRows,
    sortedBy: 'id',
    filterFn: tableFilterFn,
  });

  /** Handles webhook modals */
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteWebhookId, setDeleteWebhookId] = useState<number | null>(null);
  const openCreateModal = () => setIsCreateModalOpen(true);
  const closeCreateModal = () => setIsCreateModalOpen(false);
  const openDeleteModal = (id: number) => {
    setDeleteWebhookId(id);
    setIsDeleteModalOpen(true);
  };
  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const handleCreateWebhook = (
    params: Pick<WebhookParamProps, 'endpoint' | 'authToken' | 'authType'>,
  ) => {
    dispatch(createSystemWebhookRequest(params));
  };
  const handleDeleteWebhook = (id: number) => dispatch(deleteSystemWebhookRequest(id));

  const COLUMNS = createColumns(SystemWebhookAdvancedFiltering, openDeleteModal);

  useEffect(() => {
    dispatch(fetchSystemWebhooksRequest());
  }, []);

  return (
    <>
      <PageHeader
        documentation={{
          href: 'https://support.iterable.com/hc/en-us/articles/208013936-System-Webhooks',
          content: ' Documentation ',
        }}
        pageActions={
          <PrimaryButton onClick={openCreateModal} data-test="pageHeaderPrimaryAction">
            <AddNew16 />
            Create Webhook
          </PrimaryButton>
        }
      >
        System Webhooks
      </PageHeader>
      <CreateSystemWebhookModal
        isCreateModalOpen={isCreateModalOpen}
        onCreateModalClose={closeCreateModal}
        createWebhook={handleCreateWebhook}
      />
      <DeleteSystemWebhookModal
        isDeleteModalOpen={isDeleteModalOpen}
        deleteWebhookId={deleteWebhookId as number}
        deleteWebhook={handleDeleteWebhook}
        onDeleteModalClose={closeDeleteModal}
      />
      <FlexSpacer justifyContent="flex-end">
        <InputText
          data-test="filter-system-webhooks-input"
          placeholder="Filter by..."
          value={filter}
          onChange={setFilter}
        />
      </FlexSpacer>
      <Table data-test="system-webhooks-table">
        <TableHeader
          columns={COLUMNS}
          setSortDirection={setSortDirection}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          sortedBy={sortedBy}
        />
        {rows.length > 0 && <TableRows rows={rows} columns={COLUMNS} />}
      </Table>
      {rows.length === 0 && (
        <FlexSpacer padding="cardPadding" justifyContent="center">
          No results found
        </FlexSpacer>
      )}
      <PaginationFooter
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        numItems={numItems}
        onUpdate={({ page, pageSize }) => {
          setCurrentPage(page);
          setItemsPerPage(pageSize);
        }}
      />
    </>
  );
};

export default withRouter(SystemWebhooks);
